import { useFragment } from '@apollo/client';
import type { EnrichedParticipants, EventId } from '@ps/isp-web-services-types';
import React from 'react';

import { useLiveData } from '@swf/sports/providers/LiveDataProvider';
import type { EventTypeId } from '@swf/sports/types/eventType';
import { getEventScoreboardFragment } from '@swf/sports/utils/getEventScoreboardFragment';
import {
  getIsAmericanFootballLiveData,
  getIsBaseballLiveData,
  getIsBasketballLiveData,
  getIsDartsLiveData,
  getIsESportsLiveData,
  getIsFootballLiveData,
  getIsHandballLiveData,
  getIsIceHockeyLiveData,
  getIsSnookerLiveData,
  getIsTableTennisLiveData,
  getIsTennisLiveData,
  getIsVolleyballLiveData
} from '@swf/sports/utils/getLiveData';
import useIsEventTypeLiveDataEnabled from '@swf/sports/utils/useIsEventTypeLiveDataEnabled';

import DartsScoreboard from './components/DartsScoreboard/DartsScoreboard';
import DefaultScoreboard from './components/DefaultScoreboard/DefaultScoreboard';
import FootballScoreboard from './components/FootballScoreboard/FootballScoreboard';
import TableTennisScoreboard from './components/TableTennisScoreboard/TableTennisScoreboard';
import TennisScoreboard from './components/TennisScoreboard/TennisScoreboard';
import VolleyballScoreboard from './components/VolleyballScoreboard/VolleyballScoreboard';

export interface Props {
  eventId: EventId;
  eventTypeId: EventTypeId;
  enrichedParticipants: EnrichedParticipants | undefined;
  eventName?: string;
}

const Scoreboard: React.FC<Props> = ({ eventId, eventTypeId, enrichedParticipants, eventName }) => {
  const { variables } = useLiveData();
  const { data } = useFragment(getEventScoreboardFragment(eventTypeId, eventId, variables));
  const isEventTypeLiveDataEnabled = useIsEventTypeLiveDataEnabled(eventTypeId);

  if (!isEventTypeLiveDataEnabled || !enrichedParticipants) {
    return null;
  }

  if (
    getIsAmericanFootballLiveData(eventTypeId, data) ||
    getIsBaseballLiveData(eventTypeId, data) ||
    getIsBasketballLiveData(eventTypeId, data) ||
    getIsESportsLiveData(eventTypeId, data) ||
    getIsHandballLiveData(eventTypeId, data) ||
    getIsIceHockeyLiveData(eventTypeId, data) ||
    getIsSnookerLiveData(eventTypeId, data)
  ) {
    return (
      <DefaultScoreboard
        enrichedParticipants={enrichedParticipants}
        fixture={data}
        eventTypeId={eventTypeId}
      />
    );
  }

  if (getIsFootballLiveData(eventTypeId, data)) {
    return <FootballScoreboard enrichedParticipants={enrichedParticipants} fixture={data} />;
  }

  if (getIsTableTennisLiveData(eventTypeId, data)) {
    return <TableTennisScoreboard enrichedParticipants={enrichedParticipants} fixture={data} />;
  }

  if (getIsTennisLiveData(eventTypeId, data)) {
    return (
      <TennisScoreboard
        enrichedParticipants={enrichedParticipants}
        match={data}
        eventName={eventName}
        eventTypeId={eventTypeId}
      />
    );
  }

  if (getIsDartsLiveData(eventTypeId, data)) {
    return <DartsScoreboard enrichedParticipants={enrichedParticipants} fixture={data} />;
  }

  if (getIsVolleyballLiveData(eventTypeId, data)) {
    return <VolleyballScoreboard enrichedParticipants={enrichedParticipants} fixture={data} />;
  }

  return null;
};

export default Scoreboard;
